import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getParentByIdOptions,
	getParentByIdQueryKey,
	updateParentMutation,
	getStudentsByParentOptions,
	getStudentsByParentQueryKey,
	updatePaymentMethodMutation,
	getStripePaymentMethodsOptions,
	getStripePaymentMethodsQueryKey,
	getInvoicesByParentOptions,
	createStudentMutation,
	getSchoolsOptions,
	getLevelsOptions,
} from "@/lib/client/@tanstack/react-query.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
	ArrowLeft,
	Edit,
	Phone,
	Mail,
	MapPin,
	CreditCard,
	User,
	Receipt,
	UserPlus,
	ExternalLink,
	MessageSquare,
} from "lucide-react";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { getLevelLabel, getSchoolLabel } from "@/stores/enum-store";
import { accessStripePortal } from "@/lib/client";

export const Route = createFileRoute("/admin/parents/$id")({
	component: ParentDetailsPage,
});

function ParentDetailsPage() {
	const { id } = Route.useParams();
	const queryClient = useQueryClient();
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
	const [isCreateStudentDialogOpen, setIsCreateStudentDialogOpen] = useState(false);
	const [selectedPaymentMethod, setSelectedPaymentMethod] =
		useState<string>("");
	const [editForm, setEditForm] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		address: "",
	});
	const [studentForm, setStudentForm] = useState({
		firstName: "",
		lastName: "",
		school: "",
		schoolOther: "",
		level: "",
		hasPap: false,
		hasPps: false,
	});

	const { data: parent, isLoading: parentLoading } = useQuery(
		getParentByIdOptions({
			path: { id },
		}),
	);

	const { data: students, isLoading: studentsLoading } = useQuery(
		getStudentsByParentOptions({
			path: {
				parentId: id,
			},
		}),
	);

	const { data: stripePaymentMethods, isLoading } = useQuery({
		...getStripePaymentMethodsOptions({
			path: { id },
		}),
		enabled: !!parent?.stripeCustomerId,
	});

	const { data: invoices, isLoading: invoicesLoading } = useQuery(
		getInvoicesByParentOptions({
			path: { parentId: id },
		}),
	);

	const { data: schools } = useQuery(getSchoolsOptions());

	const { data: levels } = useQuery(getLevelsOptions());

	const updateParentMut = useMutation({
		...updateParentMutation(),
		onSuccess: () => {
			toast.success("Parent mis à jour avec succès");
			queryClient.invalidateQueries({
				queryKey: getParentByIdQueryKey({ path: { id } }),
			});
			setIsEditDialogOpen(false);
		},
		onError: () => {
			toast.error("Erreur lors de la mise à jour du parent");
		},
	});

	const createStudentMut = useMutation({
		...createStudentMutation(),
		onSuccess: () => {
			toast.success("Enfant créé avec succès");
			queryClient.invalidateQueries({
				queryKey: getStudentsByParentQueryKey({ path: { parentId: id } }),
			});
			setIsCreateStudentDialogOpen(false);
			setStudentForm({
				firstName: "",
				lastName: "",
				school: "",
				schoolOther: "",
				level: "",
				hasPap: false,
				hasPps: false,
			});
		},
		onError: () => {
			toast.error("Erreur lors de la création de l'enfant");
		},
	});

	const updatePaymentMethodMut = useMutation({
		...updatePaymentMethodMutation(),
		onSuccess: () => {
			toast.success("Méthode de paiement mise à jour avec succès");
			queryClient.invalidateQueries({
				queryKey: getParentByIdQueryKey({ path: { id } }),
			});
			queryClient.invalidateQueries({
				queryKey: getStripePaymentMethodsQueryKey({ path: { id } }),
			});
			setIsPaymentDialogOpen(false);
		},
		onError: () => {
			toast.error("Erreur lors de la mise à jour de la méthode de paiement");
		},
	});

	const handleOpenEditDialog = () => {
		if (parent) {
			setEditForm({
				firstName: parent.firstName || "",
				lastName: parent.lastName || "",
				email: parent.email || "",
				phone: parent.phone || "",
				address: parent.address || "",
			});
		}
		setIsEditDialogOpen(true);
	};

	const handleSaveEdit = () => {
		updateParentMut.mutate({
			path: { id },
			body: {
				firstName: editForm.firstName,
				lastName: editForm.lastName,
				email: editForm.email,
				phone: editForm.phone,
				address: editForm.address,
			},
		});
	};

	const handleOpenPaymentDialog = () => {
		if (parent) {
			setSelectedPaymentMethod(parent.paymentMethod || "BANK_TRANSFER");
		}
		setIsPaymentDialogOpen(true);
	};

	const handleUpdatePaymentMethod = () => {
		updatePaymentMethodMut.mutate({
			path: { id },
			body: {
				paymentMethod: selectedPaymentMethod as any,
			},
		});
	};

	const handleAccessStripePortal = () => {
		window.open(`/parent/${id}`, "_blank");
	};

	if (parentLoading || studentsLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
			</div>
		);
	}

	if (!parent) {
		return (
			<div className="p-6">
				<div className="text-center">
					<h2 className="text-2xl font-bold text-gray-900 mb-4">
						Parent non trouvé
					</h2>
					<Link to="/admin/parents">
						<Button variant="outline">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Retour à la liste
						</Button>
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<Link to="/admin/parents">
						<Button variant="outline" size="sm">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Retour
						</Button>
					</Link>
					<div>
						<h1 className="text-3xl font-bold">
							{parent.firstName} {parent.lastName}
						</h1>
						<p className="text-gray-600">Détails du parent</p>
					</div>
				</div>
				<div className="flex gap-2">
					<Link to="/admin/sms" search={{ parentIds: [id] }}>
						<Button variant="outline">
							<MessageSquare className="mr-2 h-4 w-4" />
							Envoyer SMS
						</Button>
					</Link>
					<Button onClick={handleOpenEditDialog}>
						<Edit className="mr-2 h-4 w-4" />
						Modifier
					</Button>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Informations personnelles */}
				<Card className="lg:col-span-2">
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<User className="h-5 w-5" />
							Informations personnelles
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label className="text-gray-600">Prénom</Label>
								<p className="font-medium">{parent.firstName}</p>
							</div>
							<div>
								<Label className="text-gray-600">Nom</Label>
								<p className="font-medium">{parent.lastName}</p>
							</div>
						</div>

						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<Mail className="h-4 w-4 text-gray-500" />
								<Label className="text-gray-600">Email</Label>
							</div>
							<p className="font-medium">{parent.email}</p>
						</div>

						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<Phone className="h-4 w-4 text-gray-500" />
								<Label className="text-gray-600">Téléphone</Label>
							</div>
							<p className="font-medium">{parent.phone}</p>
						</div>

						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<MapPin className="h-4 w-4 text-gray-500" />
								<Label className="text-gray-600">Adresse</Label>
							</div>
							<p className="font-medium">
								{parent.address || "Non renseignée"}
							</p>
						</div>
					</CardContent>
				</Card>

				{/* Informations de paiement */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<CreditCard className="h-5 w-5" />
							Paiement
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<Label className="text-gray-600">Moyen de paiement</Label>
							<div className="flex items-center justify-between mt-1">
								<p className="font-medium">
									{parent.paymentMethod === "CARD" && "Carte bancaire"}
									{parent.paymentMethod === "SEPA" && "Prélèvement SEPA"}
									{parent.paymentMethod === "BANK_TRANSFER" &&
										"Virement bancaire"}
									{!parent.paymentMethod && "Non défini"}
								</p>
								<Button
									size="sm"
									variant="outline"
									onClick={handleOpenPaymentDialog}
								>
									<Edit className="h-3 w-3 mr-1" />
									Modifier
								</Button>
							</div>
						</div>

						<div>
							<Label className="text-gray-600">Blocage de paiement</Label>
							<Badge
								variant={parent.paymentOnHold ? "destructive" : "default"}
								style={{ color: parent.paymentOnHold ? "black" : "white" }}
							>
								{parent.paymentOnHold ? "Actif" : "Inactif"}
							</Badge>
						</div>

						<div>
							<Label className="text-gray-600">Stripe</Label>
							<div className="flex items-center gap-2 mt-1">
								<Badge
									variant={parent.stripeCustomerId ? "default" : "destructive"}
									style={{ color: parent.stripeCustomerId ? "white" : "white" }}
								>
									{parent.stripeCustomerId ? "Actif" : "Inactif"}
								</Badge>
								{parent.stripeCustomerId && (
									<Button
										size="sm"
										variant="outline"
										onClick={handleAccessStripePortal}
									>
										<ExternalLink className="h-3 w-3 mr-1" />
										Portail client
									</Button>
								)}
							</div>
						</div>

						{parent.stripeCustomerId && (
							<div>
								<Label className="text-gray-600">
									Moyens de paiement enregistrés
								</Label>
								{isLoading && (
									<>
										<Badge variant="outline">Chargement...</Badge>
									</>
								)}
								{!isLoading && (
									<>
										<div className="mt-2 space-y-2">
											{stripePaymentMethods?.hasPaymentMethods ? (
												stripePaymentMethods.paymentMethods?.map((pm) => (
													<div
														key={pm.id}
														className="flex items-center gap-2 text-sm"
													>
														<CreditCard className="h-4 w-4 text-gray-500" />
														{pm.type === "card" && (
															<span>
																{pm.brand?.toUpperCase()} •••• {pm.last4} (
																{pm.expMonth}/{pm.expYear})
															</span>
														)}
														{pm.type === "sepa_debit" && (
															<span>SEPA •••• {pm.last4}</span>
														)}
													</div>
												))
											) : (
												<Badge variant="destructive" className="text-white">
													Aucun moyen de paiement
												</Badge>
											)}
										</div>
									</>
								)}
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Enfants */}
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<CardTitle className="flex items-center gap-2">
							<User className="h-5 w-5" />
							Enfants inscrits
						</CardTitle>
						<Button size="sm" onClick={() => setIsCreateStudentDialogOpen(true)}>
							<UserPlus className="mr-2 h-4 w-4" />
							Ajouter un enfant
						</Button>
					</div>
					<CardDescription>
						Liste des enfants rattachés à ce parent
					</CardDescription>
				</CardHeader>
				<CardContent>
					{students && students.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Prénom</TableHead>
									<TableHead>Nom</TableHead>
									<TableHead>Niveau</TableHead>
									<TableHead>École</TableHead>
									<TableHead>PAP</TableHead>
									<TableHead>PPS</TableHead>
									<TableHead className="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{students.map((student) => (
									<TableRow key={student.id}>
										<TableCell className="font-medium">
											{student.firstName}
										</TableCell>
										<TableCell>{student.lastName}</TableCell>
										<TableCell>{getLevelLabel(student.level)}</TableCell>
										<TableCell>
											{getSchoolLabel(student.school)}{" "}
											{student.school == "AUTRE" && (
												<>({student.schoolOther})</>
											)}
										</TableCell>
										<TableCell>
											<Badge variant={student.hasPap ? "default" : "secondary"}>
												{student.hasPap ? "Oui" : "Non"}
											</Badge>
										</TableCell>
										<TableCell>
											<Badge variant={student.hasPps ? "default" : "secondary"}>
												{student.hasPps ? "Oui" : "Non"}
											</Badge>
										</TableCell>
										<TableCell className="text-right">
											<Link
												to="/admin/students/$id"
												params={{ id: student.id || "" }}
											>
												<Button size="sm">Voir</Button>
											</Link>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					) : (
						<div className="text-center py-8 text-gray-500">
							Aucun enfant rattaché à ce parent
						</div>
					)}
				</CardContent>
			</Card>

			{/* Factures et paiements */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Receipt className="h-5 w-5" />
						Historique des factures
					</CardTitle>
					<CardDescription>Suivi des factures et paiements</CardDescription>
				</CardHeader>
				<CardContent>
					{invoicesLoading ? (
						<div className="text-center py-8 text-gray-500">
							Chargement de l'historique des factures...
						</div>
					) : invoices && invoices.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Numéro</TableHead>
									<TableHead>Période</TableHead>
									<TableHead>Montant</TableHead>
									<TableHead>Statut</TableHead>
									<TableHead>Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{invoices.map((invoice: any) => (
									<TableRow key={invoice.id}>
										<TableCell className="font-medium">
											{invoice.invoiceNumber}
										</TableCell>
										<TableCell>
											{new Date(invoice.billingPeriodStart).toLocaleDateString('fr-FR')} -{' '}
											{new Date(invoice.billingPeriodEnd).toLocaleDateString('fr-FR')}
										</TableCell>
										<TableCell>
											{invoice.totalAmount?.toFixed(2)} €
										</TableCell>
										<TableCell>
											<Badge
												variant={
													invoice.status === 'PAID'
														? 'default'
														: invoice.status === 'PENDING'
															? 'secondary'
															: 'destructive'
												}
											>
												{invoice.status === 'PAID' && 'Payée'}
												{invoice.status === 'PENDING' && 'En attente'}
												{invoice.status === 'FAILED' && 'Échec'}
												{invoice.status === 'PARTIALLY_PAID' && 'Partielle'}
												{invoice.status === 'CANCELLED' && 'Annulée'}
											</Badge>
										</TableCell>
										<TableCell>
											<Button
												size="sm"
												variant="outline"
												asChild
											>
												<Link to="/admin/invoices/$id" params={{ id: invoice.id }}>
													Voir
												</Link>
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					) : (
						<div className="text-center py-8 text-gray-500">
							Aucune facture trouvée pour ce parent
						</div>
					)}
				</CardContent>
			</Card>

			{/* Dialog de modification méthode de paiement */}
			<Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Modifier la méthode de paiement</DialogTitle>
						<DialogDescription>
							Choisir la méthode de paiement pour {parent.firstName}{" "}
							{parent.lastName}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<Label htmlFor="payment-method">Méthode de paiement</Label>
							<Select
								value={selectedPaymentMethod}
								onValueChange={setSelectedPaymentMethod}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="CARD">Carte bancaire</SelectItem>
									<SelectItem value="SEPA">Prélèvement SEPA</SelectItem>
									<SelectItem value="BANK_TRANSFER">
										Virement bancaire
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsPaymentDialogOpen(false)}
						>
							Annuler
						</Button>
						<Button
							onClick={handleUpdatePaymentMethod}
							disabled={updatePaymentMethodMut.isPending}
						>
							{updatePaymentMethodMut.isPending
								? "Mise à jour..."
								: "Enregistrer"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Dialog de modification */}
			<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>Modifier le parent</DialogTitle>
						<DialogDescription>
							Modifier les informations de {parent.firstName} {parent.lastName}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label htmlFor="edit-firstName">Prénom</Label>
								<Input
									id="edit-firstName"
									value={editForm.firstName}
									onChange={(e) =>
										setEditForm({ ...editForm, firstName: e.target.value })
									}
								/>
							</div>
							<div>
								<Label htmlFor="edit-lastName">Nom</Label>
								<Input
									id="edit-lastName"
									value={editForm.lastName}
									onChange={(e) =>
										setEditForm({ ...editForm, lastName: e.target.value })
									}
								/>
							</div>
						</div>

						<div>
							<Label htmlFor="edit-email">Email</Label>
							<Input
								id="edit-email"
								type="email"
								value={editForm.email}
								onChange={(e) =>
									setEditForm({ ...editForm, email: e.target.value })
								}
							/>
						</div>

						<div>
							<Label htmlFor="edit-phone">Téléphone</Label>
							<Input
								id="edit-phone"
								value={editForm.phone}
								onChange={(e) =>
									setEditForm({ ...editForm, phone: e.target.value })
								}
							/>
						</div>

						<div>
							<Label htmlFor="edit-address">Adresse</Label>
							<Textarea
								id="edit-address"
								value={editForm.address}
								onChange={(e) =>
									setEditForm({ ...editForm, address: e.target.value })
								}
								rows={3}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsEditDialogOpen(false)}
						>
							Annuler
						</Button>
						<Button
							onClick={handleSaveEdit}
							disabled={updateParentMut.isPending}
						>
							{updateParentMut.isPending ? "Enregistrement..." : "Enregistrer"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Dialog de création d'enfant */}
			<Dialog open={isCreateStudentDialogOpen} onOpenChange={setIsCreateStudentDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Ajouter un enfant</DialogTitle>
						<DialogDescription>
							Créer un nouvel enfant pour {parent.firstName} {parent.lastName}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label htmlFor="student-firstName">Prénom</Label>
								<Input
									id="student-firstName"
									value={studentForm.firstName}
									onChange={(e) =>
										setStudentForm({ ...studentForm, firstName: e.target.value })
									}
								/>
							</div>
							<div>
								<Label htmlFor="student-lastName">Nom</Label>
								<Input
									id="student-lastName"
									value={studentForm.lastName}
									onChange={(e) =>
										setStudentForm({ ...studentForm, lastName: e.target.value })
									}
								/>
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label htmlFor="student-school">École</Label>
								<Select
									value={studentForm.school}
									onValueChange={(value) =>
										setStudentForm({ ...studentForm, school: value, schoolOther: "" })
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Sélectionner une école" />
									</SelectTrigger>
									<SelectContent>
										{schools?.map((school) => (
											<SelectItem key={school.value} value={school.value}>
												{school.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label htmlFor="student-level">Niveau</Label>
								<Select
									value={studentForm.level}
									onValueChange={(value) =>
										setStudentForm({ ...studentForm, level: value })
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Sélectionner un niveau" />
									</SelectTrigger>
									<SelectContent>
										{levels?.map((level) => (
											<SelectItem key={level.value} value={level.value}>
												{level.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>

						{studentForm.school === "AUTRE" && (
							<div>
								<Label htmlFor="student-schoolOther">Nom de l'établissement</Label>
								<Input
									id="student-schoolOther"
									value={studentForm.schoolOther}
									onChange={(e) =>
										setStudentForm({ ...studentForm, schoolOther: e.target.value })
									}
									placeholder="Saisir le nom de l'établissement"
								/>
							</div>
						)}

						<div className="flex gap-4">
							<div className="flex items-center space-x-2">
								<input
									type="checkbox"
									id="hasPap"
									checked={studentForm.hasPap}
									onChange={(e) =>
										setStudentForm({ ...studentForm, hasPap: e.target.checked })
									}
									className="h-4 w-4"
								/>
								<Label htmlFor="hasPap">PAP</Label>
							</div>
							<div className="flex items-center space-x-2">
								<input
									type="checkbox"
									id="hasPps"
									checked={studentForm.hasPps}
									onChange={(e) =>
										setStudentForm({ ...studentForm, hasPps: e.target.checked })
									}
									className="h-4 w-4"
								/>
								<Label htmlFor="hasPps">PPS</Label>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsCreateStudentDialogOpen(false)}
						>
							Annuler
						</Button>
						<Button
							onClick={() => {
								createStudentMut.mutate({
									body: {
										...studentForm,
										parentId: id,
									} as any,
								});
							}}
							disabled={
								createStudentMut.isPending ||
								!studentForm.firstName ||
								!studentForm.lastName ||
								!studentForm.school ||
								!studentForm.level ||
								(studentForm.school === "AUTRE" && !studentForm.schoolOther)
							}
						>
							{createStudentMut.isPending ? "Création..." : "Créer"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
